import apiClient from "./client";

/**
 * Employee API Service
 */
export const employeeApi = {
  /**
   * Get all employees with optional filters
   * @param {Object} params - Query parameters
   * @param {boolean} params.activeOnly - Filter to active employees only
   * @param {boolean} params.troncEligibleOnly - Filter to TRONC eligible employees only
   * @param {string} params.locationId - Filter by location ID
   * @param {string} params.tenantId - Filter by tenant ID (for Global Admins)
   */
  getAll: (params = {}) => {
    return apiClient.get("/employees", params);
  },

  /**
   * Get employee by ID
   * @param {string} id - Employee ID (GUID)
   */
  getById: (id) => {
    return apiClient.get(`/employees/${id}`);
  },

  /**
   * Create a new employee
   * @param {Object} employee - Employee data
   */
  create: (employee) => {
    return apiClient.post("/employees", employee);
  },

  /**
   * Update an employee
   * @param {string} id - Employee ID
   * @param {Object} employee - Updated employee data
   */
  update: (id, employee) => {
    return apiClient.put(`/employees/${id}`, employee);
  },

  /**
   * Update employee TRONC eligibility
   * @param {string} id - Employee ID
   * @param {Object} data - Eligibility data { isEligibleForTronc, reason }
   */
  updateEligibility: (id, data) => {
    return apiClient.patch(`/employees/${id}/eligibility`, data);
  },

  /**
   * Delete an employee
   * @param {string} id - Employee ID
   * @param {string} reason - Reason for deletion
   */
  delete: (id, reason = null) => {
    return apiClient.delete(`/employees/${id}`, reason ? { reason } : {});
  },

  /**
   * Sync employees from an HR provider
   * @param {string} providerName - HR provider name (e.g., 'Sona')
   */
  syncFromProvider: (providerName) => {
    return apiClient.post(`/employees/sync/${providerName}`);
  },

  /**
   * Get available HR providers
   */
  getProviders: () => {
    return apiClient.get("/employees/providers");
  },

  /**
   * Bulk update employee eligibility
   * @param {Object} data - Bulk update data
   * @param {Array<string>} data.employeeIds - Array of employee IDs
   * @param {boolean} data.isEligibleForTronc - New eligibility status
   * @param {string} data.reason - Reason for bulk update
   */
  bulkUpdateEligibility: (data) => {
    return apiClient.put("/employees/bulk-update-eligibility", data);
  },

  /**
   * Get role history for an employee
   * @param {string} id - Employee ID
   */
  getRoleHistory: (id) => {
    return apiClient.get(`/employees/${id}/role-history`);
  },

  /**
   * Change an employee's role with history tracking
   * @param {string} id - Employee ID
   * @param {Object} data - Role change data
   * @param {string} data.newJobRoleId - New job role ID
   * @param {string} data.effectiveDate - When the change takes effect
   * @param {number} [data.contractedHoursPerWeek] - New contracted hours
   * @param {string} [data.reason] - Reason for role change
   */
  changeRole: (id, data) => {
    return apiClient.post(`/employees/${id}/role-change`, data);
  },
};

export default employeeApi;
